import { Injectable } from '@angular/core';
import moment from "moment/moment";
import { GlobalConfiguration } from "../../models/general/global-configuration";
import { CompanyTime } from "../../models/date/company-time";
import { setHours, setMinutes } from "date-fns";
@Injectable({
    providedIn: 'root'
})
export class DateService {
    constructor(
        private globalConfiguration: GlobalConfiguration
    ) { }

    userRolesWithCurrentTimeZone = ['Freelance Resource', "Inhouse Resource", "Client", "Client Contact"];

    /**
     * Resources, clients and client contacts see every time in their own browser's time zone.
     * Staff see the branch time zone (companyBranchSetting.timezoneOffsetDescription).
     */
    usesBrowserTimeZone(): boolean {
        return this.userRolesWithCurrentTimeZone.includes(this.globalConfiguration?.currentUser?.userRoleDescription);
    }

    /**
     * Parses a zone-less ISO string from the API ("2026-10-22T16:00:00", optional fraction) as UTC.
     * Falls back to the browser's own parsing for anything else (date-only, explicit offsets, Date objects).
     */
    private parseAsUtc(date: string | Date): Date {
        if (typeof date !== 'string') return new Date(date);
        const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/.exec(date);
        if (!m) return new Date(date);
        const ms = m[7] ? Number((m[7] + '00').slice(0, 3)) : 0;
        return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0), ms));
    }

    /**
     * Branch time zone value (TimezoneOffset.Description): a fixed "+hh:mm" offset, or an IANA zone id such as
     * "Europe/London" whose offset follows daylight saving and therefore depends on the date being converted.
     */
    private branchTimeZone(): string {
        return this.globalConfiguration?.companyBranchSetting?.timezoneOffsetDescription || '';
    }

    isZoneId(value: string): boolean {
        return !!value && !/^\s*[+-]?\d/.test(value);
    }

    private static zoneFormatters = new Map<string, Intl.DateTimeFormat | null>();

    /** Offset (ms) of an IANA zone at a UTC instant, via Intl. null when the browser does not know the zone. */
    private zoneOffsetAt(zone: string, utcMs: number): number | null {
        let formatter = DateService.zoneFormatters.get(zone);
        if (formatter === undefined) {
            try {
                formatter = new Intl.DateTimeFormat('en-US', <any>{
                    timeZone: zone, hourCycle: 'h23',
                    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
                });
            } catch {
                console.warn(`Unknown time zone "${zone}"; dates are shown as UTC`);
                formatter = null;
            }
            DateService.zoneFormatters.set(zone, formatter);
        }
        if (!formatter) return null;
        const parts: Record<string, number> = {};
        for (const part of formatter.formatToParts(new Date(utcMs))) {
            if (part.type !== 'literal') parts[part.type] = Number(part.value);
        }
        const wallClockAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second);
        return Math.round((wallClockAsUtc - utcMs) / 60000) * 60000;
    }

    /** "+01:00" style text for an offset in milliseconds. */
    formatOffset(offsetMs: number): string {
        const totalMinutes = Math.round(Math.abs(offsetMs) / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return (offsetMs < 0 ? '-' : '+') + ('0' + hours).slice(-2) + ':' + ('0' + minutes).slice(-2);
    }

    /**
     * Branch offset (ms) for a wall-clock time the user picked: a browser-local Date holding branch wall time. For a
     * named zone the offset is looked up at that moment and refined once, which only matters within an hour of a
     * daylight-saving switch.
     */
    private branchOffsetForWallClock(wallClock: Date): number {
        if (!this.isZoneId(this.branchTimeZone())) return this.getOffsetInMilliseconds();
        const asUtc = Date.UTC(wallClock.getFullYear(), wallClock.getMonth(), wallClock.getDate(),
            wallClock.getHours(), wallClock.getMinutes(), wallClock.getSeconds(), wallClock.getMilliseconds());
        const guess = asUtc - this.getOffsetInMilliseconds(asUtc);
        return this.getOffsetInMilliseconds(guess);
    }

    /**
     * Label for the time zone the user is looking at: "Europe/London (UTC+01:00)" or "UTC+01:00" for staff
     * (branch setting), the browser's own zone for resources and clients.
     */
    describeTimeZone(): string {
        if (this.usesBrowserTimeZone()) {
            let zone = '';
            try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { }
            const offset = `UTC${this.formatOffset(-(new Date().getTimezoneOffset() * 60000))}`;
            return zone ? `${zone} (${offset})` : offset;
        }
        const setting = this.branchTimeZone();
        if (!setting) return 'UTC';
        if (this.isZoneId(setting)) return `${setting} (UTC${this.formatOffset(this.getOffsetInMilliseconds())})`;
        return `UTC${setting}`;
    }
    /**
     * @function diffDays
     * @description find how many days difference between two dates
     * @return boolean
     */
    diffDays(fromDate: string | Date, toDate: string | Date): number {
        if (fromDate && toDate) {
            const momentFromDate = moment(fromDate);
            const momentToDate = moment(toDate);
            return momentToDate.diff(momentFromDate, 'days');
        }
        return null;
    }
    /**
     * @function diffHours
     * @description find how many hours difference between two dates
     * @return boolean
     */
    diffHours(fromDate: string | Date, toDate: string | Date): number {
        if (fromDate && toDate) {
            const momentFromDate = moment(fromDate);
            const momentToDate = moment(toDate);
            return momentToDate.diff(momentFromDate, 'hours');
        }
        return null;
    }
    /**
     * @function duration
     * @description find duration between two dates
     * @return {hours: number, minutes: number}
     */
    duration(fromDate: string | Date, toDate: string | Date): { hours: number, minutes: number } {
        if (fromDate && toDate) {
            const momentFromDate = moment(fromDate);
            const momentToDate = moment(toDate);
            const momentDuration = moment.duration(momentToDate.diff(momentFromDate));
            const hours = momentDuration.asHours();
            const minutes = momentDuration.asMinutes() % 60;
            return { hours, minutes };
        }
        return null;
    }
    /**
     * @function hasDatePassed
     * @description check if date has passed, compared with current date
     * @return boolean
     */
    hasDatePassed(date: string | Date): boolean {
        if (date) {
            const dueDate = moment(date);
            const currentDate = moment(new Date());
            const diffDays = dueDate.diff(currentDate, 'days');
            return diffDays < 0;
        }
        return null;
    }

    toUTC(date: string | Date) {
        if (date) {
            if (this.usesBrowserTimeZone()) {
                // The picked wall-clock time is in the browser's zone; the browser knows its own offset for that exact
                // date (summer or winter time), so no manual offset arithmetic is needed.
                return moment.utc(new Date(date)).format("YYYY-MM-DDTHH:mm:ss");
            }
            const originalDate = new Date(date);
            const dateMinusOffset = new Date(originalDate.getTime() - this.branchOffsetForWallClock(originalDate))
            const momentDate = moment(dateMinusOffset).format("YYYY-MM-DDTHH:mm:ss")

            return momentDate;
        }
        return date;
    }

    /**
     * @function toTimeZone
     * @param date: string
     * @description converts date to local time zone
     * @return Date
     */
    toTimeZone(date: string | Date): Date {
        if (date instanceof Date) return new Date(date);
        else if (date as any instanceof moment) return new Date(date);
        else if (date && !date?.includes('Z')) {
            // The API sends UTC without a zone marker. For browser-zone users just read it as UTC: the Date object then
            // renders in the browser's zone with the correct offset for that date, DST included.
            if (this.usesBrowserTimeZone()) return this.parseAsUtc(date);
            // Staff: shift by the branch offset in force at that UTC instant (fixed offsets are the same for every date)
            return new Date(new Date(date).getTime() + this.getOffsetInMilliseconds(this.parseAsUtc(date).getTime()));
        }
        else if (date) return new Date(date);
        else return null;
    }

    /**
     * Offset (ms) to add to a UTC instant to get the wall-clock time the user should see.
     * @param atUtcMs the UTC instant being converted (default now); only matters for zones with daylight saving.
     */
    getOffsetInMilliseconds(atUtcMs?: number): number {
        const at = atUtcMs ?? Date.now();
        if (this.usesBrowserTimeZone()) {
            // getTimezoneOffset() already includes daylight saving. An extra hour used to be added here whenever the
            // browser's zone observes DST at any point in the year, which left every resource/client in the UK or
            // Europe one hour behind all year round.
            return -(new Date(at).getTimezoneOffset() * 60 * 1000);
        }
        const setting = this.branchTimeZone();
        if (!setting) return 0;
        if (this.isZoneId(setting)) {
            return this.zoneOffsetAt(setting, at) ?? 0;
        }
        // Fixed "+hh:mm" offset
        const sign = setting.trim()[0] === '-' ? -1 : 1;
        const [hours, minutes] = setting.trim().replace(/^[+-]/, '').split(':').map(Number);
        return sign * ((hours || 0) * 60 + (minutes || 0)) * 60 * 1000;
    }

    /** Current offset as "+hh:mm" (branch setting for staff, browser for resources/clients). */
    getCurrentTimezoneOffsetToDisplay() {
        if (!this.usesBrowserTimeZone()) {
            const setting = this.branchTimeZone();
            return this.isZoneId(setting) ? this.formatOffset(this.getOffsetInMilliseconds()) : setting;
        }
        return this.formatOffset(-(new Date().getTimezoneOffset() * 60000));
    }

    /**
     * @function addTimeZoneOffset
     * @param date: string
     * @description add time zone offset to date
     * @return Date
     */
    addTimeZoneOffset(date: Date | string): Date {
        if (!date) return null;
        const dateResult = new Date(date);
        dateResult.setMinutes(dateResult.getMinutes() - (dateResult.getTimezoneOffset()));
        return dateResult;
    }
    getMomentFormat(format: string): string {
        return (format) ? format.replace('dd', 'DD').replace('mm', 'MM').replace('yyyy', 'YYYY') : '';
    }
    getDateRangePickerFormat(format: string): string {
        return (format) ? format.replace('DD', 'dd').replace('mm', 'MM').replace('YYYY', 'yy') : '';
    }
    getCompanyTime(): CompanyTime {
        const startTime = this.globalConfiguration?.companyBranchSetting?.startTimeValue ?? null;
        const endTime = this.globalConfiguration?.companyBranchSetting?.endTimeValue ?? null;
        let companyTime = new CompanyTime();
        if (startTime && startTime.includes(':')) {
            const startTimes = startTime.split(':');
            companyTime.startHours = Number(startTimes[0]);
            companyTime.startMinutes = Number(startTimes[1]);
        }
        if (endTime && endTime.includes(':')) {
            const endTimes = endTime.split(':');
            companyTime.endHours = Number(endTimes[0]);
            companyTime.endMinutes = Number(endTimes[1]);
        }
        return companyTime;
    }
    setCompanyStartTime(date: Date = new Date()): Date {
        const companyTime = this.getCompanyTime();
        return setHours(setMinutes(date, companyTime.startMinutes), companyTime.startHours);
    }
    setCompanyEndTime(date: Date = new Date()): Date {
        const companyTime = this.getCompanyTime();
        return setHours(setMinutes(date, companyTime.endMinutes), companyTime.endHours);
    }

    /**
     * @function getNewJobDates
     * @description default start/end for a job added by hand, taken from its parent (quote, project or client job).
     * Start = the parent's start, or today when the parent has none or it has already passed.
     * End = the parent's end, or the start day's end time when the parent has none or it falls before the start.
     */
    getNewJobDates(parentStart?: string | Date, parentEnd?: string | Date): { startDate: Date, endDate: Date } {
        const todayStart = this.setCompanyStartTime(new Date());
        const start = this.toTimeZone(parentStart);
        const end = this.toTimeZone(parentEnd);
        const startDate = start && start > todayStart ? start : todayStart;
        const endDate = end && end >= startDate ? end : this.setCompanyEndTime(new Date(startDate));
        return { startDate, endDate };
    }
}
